import { useEffect, useRef } from 'react'
import { useMatch } from 'react-router-dom'

import { ProfileCompletionModal } from 'components/organisms/modals/profile-completion-modal'
import { useAuth } from 'hooks/useAuth'
import { useStore } from 'hooks/useStore'
import { useProfileCompletionRequired } from 'hooks/useProfileCompletionRequired'

/**
 * Renders the profile-completion modal above every route except the landing page. Mounted once in the root
 * layout route, so a guarded page can redirect underneath it without closing it, and nothing else auto-opens
 * while it is up.
 */
export function ProfileCompletionGate() {
	const { token } = useAuth()
	const { refetchUser, loadingUser, profileStatus } = useStore()
	const required = useProfileCompletionRequired()
	// `/` is the marketing landing page for visitors, and a redirect for complete accounts: asking for profile
	// details there makes no sense. The profile still loads, so the redirected page gets the modal.
	const onLanding = useMatch({ path: '/', end: true }) !== null
	// The auth pages mount no layout that loads the profile, so an incomplete account would reach the gate
	// with no user, or with the previous account's persisted one, and never see the modal. Once per session token.
	const requestedFor = useRef<string | null>(null)

	useEffect(() => {
		if (!token || loadingUser || requestedFor.current === token) return
		requestedFor.current = token
		refetchUser().catch(() => {})
	}, [token, loadingUser, refetchUser])

	// The modal prefills from the stored user, so it must not mount from a persisted profile that may belong to
	// another account. A failed load leaves that user unconfirmed, so the modal stays closed rather than guessing.
	return required && profileStatus === 'loaded' && !onLanding ? <ProfileCompletionModal /> : null
}
